import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Check, Shield, Sparkles, CreditCard, Diamond } from 'lucide-react';
import { PAYMENT_URLS } from '@/constants/paymentUrls';

const InnerEdgePaymentPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [paymentPlan, setPaymentPlan] = useState('full'); // 'full' or 'monthly'

  const pricing = {
    full: {
      label: 'Pay in Full',
      price: 5000,
      badge: 'Best Value',
      description: 'One-time payment'
    },
    monthly: {
      label: '6-Month Payment Plan',
      price: 895,
      total: 5370,
      description: '$895/month for 6 months'
    }
  };

  const handleProceedToPayment = () => {
    // Redirect to the appropriate Ontraport payment form
    const paymentUrl = paymentPlan === 'full' 
      ? PAYMENT_URLS.innerEdge.fullPay 
      : PAYMENT_URLS.innerEdge.sixPay;
    window.location.href = paymentUrl;
  };

  const benefits = [
    {
      title: 'Grace under pressure',
      description: 'Lead with calm confidence and clarity even when chaos hits, so that you can show up steady, strategic, and unshaken no matter what\'s happening around you.'
    },
    {
      title: 'Boundaries that build respect',
      description: 'Protect your peace and your position by saying "no" with clarity and conviction, so that you teach people how to treat you—with respect, alignment, and accountability.'
    },
    {
      title: 'Influence that drives impact',
      description: 'Align your boss, team, and sponsors around your strategic priorities, so that your vision is seen, your voice is heard, and your leadership moves results that matter.'
    },
    {
      title: 'Time and energy reclaimed',
      description: 'Gain back 3–5 hours a week by eliminating burnout loops and distraction cycles, so that you can refocus your brilliance on what fuels your growth.'
    },
    {
      title: 'A life that leads too',
      description: 'Integrate purpose, wellness, and relationships into your leadership, so that success doesn\'t cost you yourself—and peace becomes your new performance metric.'
    }
  ];

  const inclusions = [
    {
      title: '6 Private Coaching Sessions',
      value: '$7,500 value',
      description: 'With a Volition ICF-certified executive coach. Each 45-minute session covers Vision Reset, Energy & Boundary Audit, Communication Refinement, Vulnerability & Value, and Next-Level Blueprint.'
    },
    {
      title: 'The Inner Edge™ Diagnostic',
      value: '$1,500 value',
      description: 'A self-leadership assessment to uncover your leadership style, stress response, and self-care gaps — so your coaching is deeply personal from day one.'
    },
    {
      title: '6 Months in Inner Circle',
      value: '$1,500 value',
      description: 'Six months of full Inner Circle membership — Soul Food, weekly reflections, accountability circles, and the wider Sister Diamonds community while you do the deeper work.'
    },
    {
      title: 'BONUS: The C-Sweet™ Consultation',
      value: '$1,500 value',
      description: 'A private 45-minute strategy session with Crystal Khalil or Dr. Nicole LaBeach. Walk away with an executive snapshot of your leadership presence and strategic insight on how to rise.'
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      
      {/* Back Link */}
      <div className="bg-forest pt-20 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/pathways" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back to Pathways</span>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-forest pb-16 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-terracotta/20 text-white border-terracotta mb-6">Where Power Meets Peace</Badge>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            The Inner Edge™
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Protect Your Peace. Elevate Your Power.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Left Column - Program Details */}
          <div>
            {/* Intro */}
            <div className="mb-10">
              <p className="text-lg text-gray-700 leading-relaxed">
                In a world where the rules keep changing—and women are still expected to hold it all together—
                <strong> The Inner Edge</strong> gives you the strategy, space, and sisterhood to lead without losing yourself.
              </p>
              <p className="text-lg text-gray-700 mt-4 leading-relaxed">
                You've earned your seat. Now it's time to lead from your center, not your stress.
              </p>
            </div>

            {/* Benefits */}
            <div className="mb-10">
              <h2 className="font-heading text-2xl font-bold text-forest mb-6">Here's what you'll master:</h2>
              <div className="space-y-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="flex gap-4">
                    <Diamond className="h-6 w-6 text-terracotta flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-forest">{benefit.title}</h3>
                      <p className="text-gray-600 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote */}
            <div className="bg-forest/5 rounded-xl p-6 mb-10">
              <p className="text-forest italic text-lg">
                "Because true power isn't about doing more—it's about <strong>becoming more aligned.</strong>"
              </p>
              <p className="text-gray-600 mt-3">
                When you lead from your Inner Edge, calm becomes your strategy, clarity becomes your advantage, and peace becomes your proof.
              </p>
            </div>

            {/* What's Included */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-forest mb-6">What's Included:</h2>
              <div className="space-y-6">
                {inclusions.map((item, i) => (
                  <Card key={i} className="border-l-4 border-l-terracotta">
                    <CardContent className="p-5">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-forest">{item.title}</h3>
                        <Badge variant="outline" className="text-terracotta border-terracotta">{item.value}</Badge>
                      </div>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-2xl font-bold text-forest">Total Value: $12,000+</p>
                <p className="text-terracotta font-semibold">Your Investment: $5,000</p>
              </div>
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div>
            <Card className="sticky top-24 shadow-xl border-2 border-forest/10">
              <CardContent className="p-6 md:p-8">
                <h2 className="font-heading text-2xl font-bold text-forest mb-6 text-center">
                  Complete Your Enrollment
                </h2>

                {/* Payment Plan Selection */}
                <div className="mb-8">
                  <Label className="text-base font-semibold text-forest mb-4 block">Select Payment Option</Label>
                  <RadioGroup value={paymentPlan} onValueChange={setPaymentPlan} className="space-y-3">
                    <div 
                      className={`relative flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentPlan === 'full' ? 'border-forest bg-forest/5' : 'border-gray-200 hover:border-forest/50'
                      }`}
                      onClick={() => setPaymentPlan('full')}
                    >
                      <RadioGroupItem value="full" id="full" className="mr-4" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="full" className="font-semibold text-forest cursor-pointer">Pay in Full</Label>
                          <Badge className="bg-terracotta text-white text-xs">Best Value</Badge>
                        </div>
                        <p className="text-2xl font-bold text-forest mt-1">$5,000</p>
                      </div>
                    </div>
                    
                    <div 
                      className={`relative flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        paymentPlan === 'monthly' ? 'border-forest bg-forest/5' : 'border-gray-200 hover:border-forest/50'
                      }`}
                      onClick={() => setPaymentPlan('monthly')}
                    >
                      <RadioGroupItem value="monthly" id="monthly" className="mr-4" />
                      <div className="flex-1">
                        <Label htmlFor="monthly" className="font-semibold text-forest cursor-pointer">6-Month Payment Plan</Label>
                        <p className="text-2xl font-bold text-forest mt-1">$895<span className="text-base font-normal text-gray-500">/month</span></p>
                        <p className="text-sm text-gray-500">Total: $5,370</p>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* Order Summary */}
                <div className="bg-sand/30 rounded-xl p-4 mb-6">
                  <h3 className="font-semibold text-forest mb-3">Order Summary</h3>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">The Inner Edge™</span>
                    <span className="font-medium">${paymentPlan === 'full' ? '5,000' : '895/mo'}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-forest">
                        {paymentPlan === 'full' ? 'Total' : 'First Payment'}
                      </span>
                      <span className="font-bold text-xl text-forest">
                        ${paymentPlan === 'full' ? '5,000' : '895'}
                      </span>
                    </div>
                    {paymentPlan === 'monthly' && (
                      <p className="text-xs text-gray-500 mt-1">Then $895/month for 5 more months (Total: $5,370)</p>
                    )}
                  </div>
                </div>

                {/* Proceed to Payment Button */}
                <Button 
                  onClick={handleProceedToPayment}
                  className="w-full btn-primary h-14 text-lg"
                  data-testid="proceed-to-payment-btn"
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  Proceed to Secure Checkout
                </Button>
                
                {/* Security Badge */}
                <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mt-4">
                  <Shield className="h-4 w-4" />
                  <span>Secure payment processing</span>
                </div>

                <p className="text-xs text-gray-400 text-center mt-4">
                  You will be redirected to our secure payment portal to complete your enrollment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-forest/5 rounded-2xl p-8">
          <p className="text-lg text-gray-700 italic max-w-3xl mx-auto">
            "This is not professional development. This is personal freedom. This isn't a corporate program.
            This is for the woman who knows: <strong>'My next level of leadership requires more than strategy — it requires self-realignment.'</strong>"
          </p>
          <p className="text-gray-600 mt-4">
            Whether you're managing a team, leading a department, or running the show — if you're tired, unclear, or shrinking behind your success, this is your space to reset, refocus, and rise.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InnerEdgePaymentPage;
